import { clientReducerActions } from "../../../_services/store";
import * as clientService from "./clients.service";
import {
    getClientByExternalUUIDFromClients,
    getModulesToActivateForConnection,
    mapConnectedAuthDataPropsToDTO,
} from "./client.mapper";
import { AuthDataIdentifier } from "../../../_statics/AuthDataType";
import { Modes } from "../../../_statics/CRUDStatics";
import stringUtils from "../../../_services/string-utils";
import { organizationService } from "../../myOrganization/services/organization.service";
import { reloadUserData } from "../../../user/services/store/authentication.actions";

export const getClientsForSelectedOrganization = () => {
    return async (dispatch) => {
        dispatch(clientReducerActions.getClientsForSelectedOrganizationRequest());
        try {
            const clients = await clientService.getClientsForSelectedOrganization();
            dispatch(clientReducerActions.getClientsForSelectedOrganizationSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.getClientsForSelectedOrganizationFailure(error));
        }
    };
};

// Same fetch without the loading flag and without clearing the error messages, so a background poll (the
// setup card waiting for a connection to be confirmed) doesn't blank out the screen that triggered it
export const refreshClientsQuietly = () => {
    return async (dispatch) => {
        try {
            const clients = await clientService.getClientsForSelectedOrganization();
            dispatch(clientReducerActions.getClientsForSelectedOrganizationSuccess(clients));
            return true;
        } catch (error) {
            // Background refresh — keeps whatever is on screen, but reports the failure so a poll that
            // keeps failing can give up instead of waiting out its deadline in silence
            return false;
        }
    };
};

export const getClientsForParallelTestOrganization = (organizationUUID) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.getClientsForParallelTestOrganizationRequest());
        try {
            const clients = await clientService.getClientsForParallelTestOrganization(organizationUUID);
            dispatch(clientReducerActions.getClientsForParallelTestOrganizationSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.getClientsForParallelTestOrganizationFailure(error));
        }
    };
};

export const createClientForSelectedOrganization = (client) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.createClientRequest());
        try {
            const clients = await clientService.createClient(client);
            dispatch(clientReducerActions.createClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.createClientFailure(error));
        }
    };
};

export const updateClient = (client) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.updateClientRequest());
        try {
            const clients = await clientService.updateClient(client);
            dispatch(clientReducerActions.updateClientSuccess(clients));
            return getClientByExternalUUIDFromClients(clients, client.externalUUID);
        } catch (error) {
            dispatch(clientReducerActions.updateClientFailure(error));
        }
    };
};

export const updateClientDatevSettings = (clientData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.updateClientRequest());
        try {
            const clients = await clientService.updateClientDatevSettings(clientData);
            dispatch(clientReducerActions.updateClientDatevSettingsSuccess(clients));
            return clients;
        } catch (error) {
            dispatch(clientReducerActions.updateClientFailure(error));
            throw error;
        }
    };
};

export const deleteClient = (client) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.deleteClientRequest());
        try {
            const clients = await clientService.deleteClient(client.externalUUID);
            dispatch(clientReducerActions.deleteClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.deleteClientFailure(error));
        }
    };
};

export const createConnectedAuthDataForClient = (connectedAuthData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.createConnectedAuthDataForClientRequest());
        try {
            const clients = await clientService.createConnectedAuthDataForClient(connectedAuthData);
            dispatch(clientReducerActions.createConnectedAuthDataForClientSuccess(clients));
            dispatch(activateModuleForConnection(connectedAuthData.type));
        } catch (error) {
            dispatch(clientReducerActions.createConnectedAuthDataForClientFailure(error));
        }
    };
};

// Activation posts the organization's full allowedApps, so two of these in flight for the same
// organization would each drop what the other added. The connection modal is no help: its create thunk
// clears isEditAuthDataLoading and closes the modal before dispatching the activation, and does not await
// it, so a second connection can be started while the first update is still running. Activations are
// therefore queued per organization.
const activationQueues = new Map();

const enqueueActivation = (organizationUUID, activation) => {
    const previous = activationQueues.get(organizationUUID) || Promise.resolve();
    // Runs on both settle paths: a failed activation must not stall every later one.
    const current = previous.then(activation, activation);
    activationQueues.set(organizationUUID, current);
    current.finally(() => {
        // Only the tail clears the entry, so the map does not grow per organization for the session.
        if (activationQueues.get(organizationUUID) === current) {
            activationQueues.delete(organizationUUID);
        }
    });
    return current;
};

// Auto-activates the module(s) behind a freshly created credential-based connection so that the
// downstream import/config screens (guarded by allowedApps) are immediately usable (issue #41843).
// OAuth-based connections already auto-activate their module on the backend, so those are skipped.
export const activateModuleForConnection = (type) => {
    return async (dispatch, getState) => {
        const organizationUUID = getState().authentication.user?.selectedOrganization?.externalUUID;
        if (!organizationUUID) {
            return;
        }
        return enqueueActivation(organizationUUID, async () => {
            // Read the organization when this activation's turn comes up rather than when it was queued:
            // one waiting behind another has to see the apps that one added.
            const selectedOrganization = getState().authentication.user?.selectedOrganization;
            if (!selectedOrganization) {
                return;
            }
            const modulesToActivate = getModulesToActivateForConnection(type, selectedOrganization.allowedApps);
            if (modulesToActivate.length === 0) {
                return;
            }
            try {
                const updatedAllowedApps = [...(selectedOrganization.allowedApps || []), ...modulesToActivate];
                // The endpoint binds UpdateOrganizationAllowedAppsDto — a bare array does not deserialize.
                await organizationService.updateAllowedApps(selectedOrganization.externalUUID, {
                    allowedApplications: updatedAllowedApps,
                });
                // Awaited inside the queue so the next activation reads the updated allowedApps.
                await dispatch(reloadUserData());
            } catch (error) {
                // Non-fatal: the connection was created successfully; the module can still be activated
                // manually under "Weitere Integrationen" if the auto-activation call fails.
                console.error("Auto-activation of module for connection failed:", type, error);
            }
        });
    };
};

export const updateConnectedAuthDataForClient = (connectedAuthData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.updateConnectedAuthDataForClientRequest());
        try {
            const clients = await clientService.updateConnectedAuthDataForClient(connectedAuthData);
            dispatch(clientReducerActions.updateConnectedAuthDataForClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.updateConnectedAuthDataForClientFailure(error));
        }
    };
};

export const deleteConnectedAuthDataForClient = (connectedAuthData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.deleteConnectedAuthDataForClientRequest());
        try {
            const clients = await clientService.deleteConnectedAuthDataForClient(connectedAuthData);
            dispatch(clientReducerActions.deleteConnectedAuthDataForClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.deleteConnectedAuthDataForClientFailure(error));
        }
    };
};

export const prepareAuthDataOnboarding = (authDataType, request, shouldCommit) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.prepareAuthDataOnboardingRequest());
        try {
            let prepareOnboardingResponse;
            if (authDataType === AuthDataIdentifier.PAYPAL) {
                prepareOnboardingResponse = await clientService.preparePaypalConnectOnboarding(request);
            }

            // Optional commit gate for the connected-auth race guard [issue-42708]: skip the success commit when
            // the originating client/type is no longer selected. Omitted by existing callers, so they commit as before.
            if (!shouldCommit || shouldCommit()) {
                dispatch(
                    clientReducerActions.prepareAuthDataOnboardingSuccess(
                        prepareOnboardingResponse.authLink ? prepareOnboardingResponse.authLink : prepareOnboardingResponse
                    )
                );
            }

            return prepareOnboardingResponse;
        } catch (error) {
            // Same commit gate as the success path [issue-42708]: a stale rejection must not overwrite the
            // now-selected client's error state after the selection has moved on. Mirrors the guarded eBay catch.
            if (shouldCommit && !shouldCommit()) {
                return;
            }
            dispatch(clientReducerActions.prepareAuthDataOnboardingFailure(error));
            throw error;
        }
    };
};

export const getDatevConnections = () => {
    return async (dispatch) => {
        dispatch(clientReducerActions.getDatevConnectionsRequest());
        try {
            const response = await clientService.getDatevConnections();
            dispatch(clientReducerActions.getDatevConnectionsSuccess(response));
        } catch (error) {
            dispatch(clientReducerActions.getDatevConnectionsFailure(error));
        }
    };
};

export const prepareDatevUOOnboarding = (request) => {
    return async (dispatch) => {
        dispatch(
            clientReducerActions.prepareDatevUOOnboardingRequest(request.consultantNumber + "-" + request.clientNumber)
        );
        try {
            const prepareOnboardingResponse = await clientService.prepareDatevUOOnboarding(request);
            dispatch(
                clientReducerActions.prepareDatevUOOnboardingSuccess(
                    prepareOnboardingResponse.authLink ? prepareOnboardingResponse.authLink : prepareOnboardingResponse
                )
            );

            return prepareOnboardingResponse;
        } catch (error) {
            dispatch(clientReducerActions.prepareDatevUOOnboardingFailure(error));
            throw error;
        }
    };
};

export const removeDatevConnection = (request) => {
    return async (dispatch) => {
        dispatch(
            clientReducerActions.removeDatevConnectionRequest(request.consultantNumber + "-" + request.clientNumber)
        );
        try {
            const datevId = await clientService.removeDatevConnection(request);
            dispatch(clientReducerActions.removeDatevConnectionSuccess(datevId));
        } catch (error) {
            dispatch(clientReducerActions.removeDatevConnectionFailure(error));
        }
    };
};

export const addConnectedAuthDataId = (clientUUID, authDataId) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.connectAuthDataWithClientRequest());
        try {
            await clientService.addConnectedAuthDataId({
                connectedAuthDataExternalUUID: authDataId,
                selectedPlatformClientExternalUUID: clientUUID,
            });
            dispatch(clientReducerActions.connectAuthDataWithClientSuccess());
        } catch (error) {
            dispatch(clientReducerActions.connectAuthDataWithClientFailure(error));
        }
    };
};

export const createNewConnectedAuthDataForClient = (client, initialConnectedAuthData) => {
    return (dispatch) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
        const preparedConnectedAuthData = { ...initialConnectedAuthData };
        preparedConnectedAuthData.platformClientExternalUUID = client.externalUUID;
        dispatch(clientReducerActions.setEditConnectedAuthData(preparedConnectedAuthData));
        dispatch(clientReducerActions.toggleEditConnectedAuthDataModalForClient());
    };
};

export const createNewConnectedAuthDataForClientFromRedirect = (client, initialConnectedAuthData) => {
    // Perform create AuthData from redirect - without opening the create-connection-modal.
    return (dispatch) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
        const preparedConnectedAuthData = { ...initialConnectedAuthData };
        preparedConnectedAuthData.platformClientExternalUUID = client.externalUUID;
        dispatch(clientReducerActions.setEditConnectedAuthData(preparedConnectedAuthData));
    };
};

// Race guard for connected-auth redirects: only commit an async provider's link when the client/type that
// started the request is still selected, so a late-resolving stale request can't overwrite it [issue-42708].
// Opt-in per caller via guardSelection: the /clients dropdown (which tracks the selection in editConnectedAuthData)
// enables it; callers that connect a single fixed client (IntegrationSetupCard) leave it off and always commit.
const isSelectionCurrent = (getState, clientUUID, type) => {
    const selection = getState().client.editConnectedAuthData;
    return !!selection && selection.clientUUID === clientUUID && selection.type === type;
};

export const getRedirectingLinkBySelectedAuthDataType = (connectedAuthDataType, clientUUID, guardSelection = false) => {
    return (dispatch, getState) => {
        if (connectedAuthDataType === AuthDataIdentifier.AMAZON) {
            return (
                "https://sellercentral.amazon.com/apps/authorize/consent?application_id=" +
                process.env.REACT_APP_AMAZON_APPLICATION_ID +
                "&state=" +
                clientUUID +
                "&version=beta" +
                "&redirect_uri=" +
                process.env.REACT_APP_API_BASE_URL +
                "/oauth/amazon/connect"
            );
        } else if (connectedAuthDataType === AuthDataIdentifier.MOLLIE) {
            return (
                "https://www.mollie.com/oauth2/authorize" +
                "?response_type=code" +
                "&approval_prompt=force" +
                "&client_id=" +
                process.env.REACT_APP_MOLLIE_CLIENT_ID +
                "&state=" +
                clientUUID +
                "&redirect_uri=" +
                process.env.REACT_APP_API_BASE_URL +
                "/oauth/mollie/connect" +
                "&scope=payments.read refunds.read customers.read profiles.read invoices.read settlements.read orders.read balances.read balance-reports.read organizations.read onboarding.read"
            );
        } else if (connectedAuthDataType === AuthDataIdentifier.OTTO_OAUTH) {
            return process.env.REACT_APP_OTTO_INSTALLATION_LINK + "?state=" + clientUUID;
        } else if (connectedAuthDataType === AuthDataIdentifier.EBAY_OAUTH) {
            clientService.getEbayOAuthState(clientUUID).then((response) => {
                if (guardSelection && !isSelectionCurrent(getState, clientUUID, AuthDataIdentifier.EBAY_OAUTH)) return;
                const stateToken = response.data.state;
                const authLink = process.env.REACT_APP_EBAY_AUTHORIZATION_LINK +
                    "&state=" + encodeURIComponent(stateToken);
                dispatch(clientReducerActions.setAuthLink(authLink));
            }).catch(() => {
                if (guardSelection && !isSelectionCurrent(getState, clientUUID, AuthDataIdentifier.EBAY_OAUTH)) return;
                // Fallback: use clientUUID directly if state endpoint fails
                const authLink = process.env.REACT_APP_EBAY_AUTHORIZATION_LINK +
                    "&state=" + clientUUID;
                dispatch(clientReducerActions.setAuthLink(authLink));
            });
            return null; // async — link will be set via dispatch once state token is fetched
        } else if (connectedAuthDataType === AuthDataIdentifier.PAYPAL) {
            dispatch(
                prepareAuthDataOnboarding(
                    connectedAuthDataType,
                    {
                        externalUUID: clientUUID,
                    },
                    guardSelection ? () => isSelectionCurrent(getState, clientUUID, connectedAuthDataType) : undefined
                )
            );
            return null; // Paypal needs some time to preprocess. Clear the auth link until a successful response will re-set it
        } else if (connectedAuthDataType === AuthDataIdentifier.STRIPE_CONNECT) {
            return (
                "https://connect.stripe.com/oauth/authorize" +
                "?response_type=code" +
                "&client_id=" +
                process.env.REACT_APP_STRIPE_CLIENT_ID +
                "&redirect_uri=" +
                process.env.REACT_APP_API_BASE_URL +
                "/oauth/stripe/connect" +
                "&state=" +
                clientUUID
            );
        } else if (connectedAuthDataType === AuthDataIdentifier.SHOPIFY_APPSTORE_BILLING) {
            return "https://apps.shopify.com/pathway-rechnungsprinter?locale=de&surface_intra_position=2&surface_type=partners&surface_version=redesign";
        } else if (connectedAuthDataType === AuthDataIdentifier.SHOPIFY_APPSTORE_PLATFORM) {
            return "https://apps.shopify.com/pathway-solutions?locale=de&surface_intra_position=1&surface_type=partners&surface_version=redesign";
        } else if (connectedAuthDataType === AuthDataIdentifier.TIKTOK_OAUTH) {
            return process.env.REACT_APP_TIK_TOK_AUTHORIZATION_LINK + "&state=" + clientUUID;
        }
    };
};

export const searchClientsFromProd = (request) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.searchClientsFromProdRequest());
        try {
            const prodClients = await clientService.searchClientsFromProd(request);
            dispatch(clientReducerActions.searchClientsFromProdSuccess(prodClients));
        } catch (error) {
            dispatch(clientReducerActions.searchClientsFromProdFailure(error));
        }
    };
};

export const autoUpdateClientFromProd = (request) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.autoUpdateClientFromProdRequest());
        try {
            const clients = await clientService.autoUpdateClientFromProd(request);
            dispatch(clientReducerActions.autoUpdateClientFromProdSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.autoUpdateClientFromProdFailure(error));
        }
    };
};

export const submitEditConnectedAuthData = (clientReducer) => {
    return (dispatch) => {
        const connectedAuthDataToBeUpdated = { ...clientReducer.editConnectedAuthData };
        const updatedAccessData = clientReducer.editAccessData;
        if (clientReducer.editConnectedAuthDataMode === Modes.CREATE) {
            dispatch(createConnectedAuthDataForClient(mapConnectedAuthDataPropsToDTO(connectedAuthDataToBeUpdated)));
        } else {
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.endpoint.displayName) &&
                !stringUtils.isBlank(updatedAccessData.endpoint) &&
                !clientReducer.inputLableInformation.endpoint.disabled
            ) {
                connectedAuthDataToBeUpdated.endpoint = updatedAccessData.endpoint;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.key.displayName) &&
                !stringUtils.isBlank(updatedAccessData.key)
            ) {
                connectedAuthDataToBeUpdated.key = updatedAccessData.key;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.value.displayName) &&
                !stringUtils.isBlank(updatedAccessData.value)
            ) {
                connectedAuthDataToBeUpdated.value = updatedAccessData.value;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.token.displayName) &&
                !stringUtils.isBlank(updatedAccessData.token)
            ) {
                connectedAuthDataToBeUpdated.token = updatedAccessData.token;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.sharedSecret.displayName) &&
                !stringUtils.isBlank(updatedAccessData.sharedSecret)
            ) {
                connectedAuthDataToBeUpdated.token = updatedAccessData.token;
            }
            dispatch(updateConnectedAuthDataForClient(mapConnectedAuthDataPropsToDTO(connectedAuthDataToBeUpdated)));
        }
    };
};
